/** @jsxImportSource @emotion/react */
import { Interpolation, SerializedStyles, Theme, css } from '@emotion/react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useEffect, useCallback, useRef, useState, useMemo, Fragment, ReactNode } from 'react';
import { Box, ButtonBase, Popover } from '@mui/material';
import usePrevious from '../../hooks/usePrevious';
import { PaginatedQueryRequest, QueryRequest } from '../../typings/request';
import { QSButton, QSTypography } from '../../components/atoms';
import Loader from '../../components/molecules/loader';
import EmptyDataMessage from '../empty-data-message';
import CloseIcon from '@mui/icons-material/Close';
import { QSCheckbox } from '../../components/atoms/form';
import ArrowExpandIcon from '../../icons/icons-revamp/arrow_expand';
import QSSearchBar from '../../components/molecules/search-bar';

const DropdownVirtualListWithConfirm = <
  T extends { id: V; [key: string]: any },
  K extends { hasMore: boolean },
  V = string,
  Multiple = boolean
>({
  disabled,
  popoverLabel,
  popoverValue,
  searchTerm,
  setSearchTerm,
  searchInputValue,
  placeholder,
  optionsEmptyMessage,
  name,
  value,
  onChange,
  closeOnSelect,
  multiple,
  allValuesId,
  disablePortal,
  fetchMore,
  requestData,
  refresh,
  containerStyles,
  popoverLabelStyles,
  placeholderStyles,
  buttonStyles,
  emptyDataMessageStyles,
  emptyDataContainerStyles,
  showCloseIcon,
  onCloseIconClick,
  onDropdownOpen,
  onDropdownClose,
  allowNoSelection = true,
  customHandleChange,
  caretIconCss,
  hoverBorderColor = '#2d2d2d',
  disableSearch = false,
  customButton,
  customButtonBottom,
  open,
  paperWidth,
  paperHeight,
  showCancelConfirmButtons = false,
  onCancel,
  onConfirm,
  isConfirmDisabled,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  moveSelectedToTop = false,
  pinnedTopItemIds,
  renderItemPrefix,
}: {
  disabled?: boolean;
  popoverLabel?: string;
  popoverValue?: string;
  searchTerm?: string;
  setSearchTerm?: (searchTerm: string) => void;
  searchInputValue?: string;
  placeholder?: string;
  optionsEmptyMessage?: string;
  name: (keyof T)[];
  value: Multiple extends true ? V[] : V;
  onChange: Multiple extends true ? (value: V[]) => void : (value: V) => void;
  closeOnSelect?: boolean;
  multiple?: Multiple;
  allValuesId?: V;
  disablePortal?: boolean;
  fetchMore?: () => Promise<void>;
  requestData?: PaginatedQueryRequest<T, K> | QueryRequest<T[]>;
  refresh?: () => Promise<T[] | void | undefined>;
  containerStyles?: SerializedStyles;
  popoverLabelStyles?: SerializedStyles;
  placeholderStyles?: SerializedStyles;
  buttonStyles?: SerializedStyles;
  emptyDataMessageStyles?: SerializedStyles;
  emptyDataContainerStyles?: SerializedStyles;
  showCloseIcon?: boolean;
  onCloseIconClick?: () => void;
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
  allowNoSelection?: boolean;
  customHandleChange?: (item: T) => void;
  caretIconCss?: Interpolation<Theme>;
  hoverBorderColor?: string;
  disableSearch?: boolean;
  customButton?: ReactNode;
  customButtonBottom?: ReactNode;
  open?: boolean;
  paperWidth?: string;
  paperHeight?: string;
  showCancelConfirmButtons?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  isConfirmDisabled?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  moveSelectedToTop?: boolean;
  pinnedTopItemIds?: V[];
  renderItemPrefix?: (item: T) => ReactNode;
}) => {
  const anchorEl = useRef<HTMLDivElement>(null);

  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [pendingValue, setPendingValue] = useState<Multiple extends true ? V[] : V | null>(
    null as Multiple extends true ? V[] : V | null
  );
  const [originalValue, setOriginalValue] = useState<Multiple extends true ? V[] : V | null>(
    null as Multiple extends true ? V[] : V | null
  );

  // Use external open prop if provided, otherwise use internal state
  const isDropdownOpen = open !== undefined ? open : dropdownVisible;

  const prevSearchTerm = usePrevious<string | undefined>(searchTerm);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const virtualListRef = useRef<VirtuosoHandle>(null);
  const prevIsDropdownOpen = useRef(isDropdownOpen);

  // Initialize pending value when dropdown opens
  useEffect(() => {
    // Only initialize when dropdown opens (transition from closed to open)
    if (isDropdownOpen && !prevIsDropdownOpen.current && showCancelConfirmButtons) {
      setPendingValue(value);
      setOriginalValue(value);
    }
    prevIsDropdownOpen.current = isDropdownOpen;
  }, [isDropdownOpen, showCancelConfirmButtons, value]);

  useEffect(() => {
    if (virtualListRef.current) {
      virtualListRef.current.scrollToIndex({
        index: 0,
        behavior: 'auto',
      });
    }
  }, [virtualListRef, searchTerm]);

  const getDisplayName = useCallback(
    (item: T) => {
      for (const index in name) {
        if (['string', 'number', 'boolean'].indexOf(typeof item[name[index]]) > -1) {
          return String(item[name[index]]);
        }
      }
      return String(item.id);
    },
    [name]
  );

  // Generic sorting function to move selected items to top
  const sortDataWithSelectedFirst = useCallback(
    (data: T[], selectedValues: Multiple extends true ? V[] : V): T[] => {
      if (!moveSelectedToTop) return data;

      // Separate pinned items from the rest
      const pinnedSet = new Set(pinnedTopItemIds ?? []);
      const pinnedItems = data.filter((item) => pinnedSet.has(item.id));
      const nonPinnedData = data.filter((item) => !pinnedSet.has(item.id));

      const selectedSet = multiple ? new Set(selectedValues as V[]) : new Set([selectedValues as V]);

      const sortedNonPinned = [...nonPinnedData].sort((a, b) => {
        const aSelected = selectedSet.has(a.id);
        const bSelected = selectedSet.has(b.id);

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        // Both selected or both not selected - sort alphabetically by display name
        const aName = getDisplayName(a).toLowerCase();
        const bName = getDisplayName(b).toLowerCase();
        return aName.localeCompare(bName);
      });

      return [...pinnedItems, ...sortedNonPinned];
    },
    [moveSelectedToTop, multiple, getDisplayName, pinnedTopItemIds]
  );

  const filteredRequestData = useMemo(() => {
    let data = requestData?.data ?? [];

    // Apply search filter if not disabled and term has at least 1 character
    const term = searchTerm?.trim().toLowerCase() ?? '';

    if (!disableSearch && term.length >= 1) {
      data = data.filter((item) => {
        // Check each name property in order (same as getDisplayName logic)
        for (const key of name) {
          const itemValue = item[key];
          // Only check string, number, or boolean values
          if (itemValue !== null && itemValue !== undefined) {
            const valueType = typeof itemValue;
            if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
              if (String(itemValue).toLowerCase().includes(term)) {
                return true;
              }
            }
          }
        }
        // No match found in provided name fields
        return false;
      });
    }

    // Apply selected items sorting if enabled
    if (moveSelectedToTop) {
      data = sortDataWithSelectedFirst(data, value);
    }

    return data;
  }, [requestData?.data, searchTerm, disableSearch, name, moveSelectedToTop, value, sortDataWithSelectedFirst]);

  const handleDropdownOpen = useCallback(() => {
    setDropdownVisible(true);
    onDropdownOpen?.();
  }, [setDropdownVisible, onDropdownOpen]);

  const handleDropdownClose = useCallback(() => {
    setDropdownVisible(false);
    if (showCancelConfirmButtons) {
      // Reset pending value on close if Cancel/Confirm mode
      setPendingValue(null as Multiple extends true ? V[] : V | null);
      setOriginalValue(null as Multiple extends true ? V[] : V | null);
    }
    // Search term is now managed by parent components via atoms, don't reset here
    onDropdownClose?.();
  }, [setDropdownVisible, onDropdownClose, showCancelConfirmButtons]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    handleDropdownClose();
  }, [onCancel, handleDropdownClose]);

  const onEscapeKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (isDropdownOpen && event.key === 'Escape') {
        if (showCancelConfirmButtons) {
          handleCancel();
        } else {
          handleDropdownClose();
        }
      }
    },
    [isDropdownOpen, handleDropdownClose, showCancelConfirmButtons, handleCancel]
  );

  useEffect(() => {
    if (!disableSearch && typeof prevSearchTerm === 'string' && prevSearchTerm !== searchTerm) {
      refresh?.();
    }
  }, [searchTerm, prevSearchTerm, refresh, disableSearch]);

  useEffect(() => {
    document.addEventListener('keydown', onEscapeKeyPress);
    return () => {
      document.removeEventListener('keydown', onEscapeKeyPress);
    };
  }, [onEscapeKeyPress]);

  const handleChange = useCallback(
    (item: T) => {
      if (showCancelConfirmButtons) {
        // In Cancel/Confirm mode, update pending value instead of calling onChange
        if (multiple) {
          setPendingValue((prev) => {
            const prevArray = (prev as V[]) ?? [];
            const hasAll = typeof allValuesId === 'string' && allValuesId.length > 0 && prevArray.includes(allValuesId);

            // Handle "All" option click
            if (typeof allValuesId === 'string' && allValuesId.length > 0 && item.id === allValuesId) {
              // If "All" is already selected, unselect everything
              if (hasAll) {
                return [] as Multiple extends true ? V[] : V;
              } else {
                // Select "All" (which means all items are selected in UI)
                return [allValuesId] as Multiple extends true ? V[] : V;
              }
            }

            // Handle individual item click
            if (hasAll) {
              // When "All" is selected, clicking any item means:
              // Remove "All" and the clicked item, but keep all other items selected
              // Get all available item IDs (excluding "All" and the clicked item)
              const allItemIds = (requestData?.data ?? [])
                .map((dataItem) => dataItem.id)
                .filter((id) => id !== allValuesId && id !== item.id) as V[];
              return allItemIds as Multiple extends true ? V[] : V;
            } else {
              // Normal case: "All" is not selected
              const isItemSelected = prevArray.indexOf(item.id) > -1;

              if (isItemSelected) {
                // Item is in the array, so unselect it
                const newArray = prevArray.filter((id) => id !== item.id);
                return newArray as Multiple extends true ? V[] : V;
              } else {
                // Item is not in the array, so select it
                return [...new Set([...prevArray, item.id])] as Multiple extends true ? V[] : V;
              }
            }
          });
        } else {
          // Single select mode: if clicking the already selected item, select allValuesId (if provided)
          if (item.id === pendingValue && typeof allValuesId === 'string' && allValuesId.length > 0) {
            setPendingValue(allValuesId as Multiple extends true ? V[] : V);
          } else {
            setPendingValue(item.id as Multiple extends true ? V[] : V);
          }
        }
        if (closeOnSelect) {
          handleDropdownClose();
        }
      } else {
        // Immediate apply mode - call onChange immediately
        if (multiple) {
          const currentArray = (value as V[]) ?? [];
          const hasAll =
            typeof allValuesId === 'string' && allValuesId.length > 0 && currentArray.includes(allValuesId);

          // Handle "All" option click
          if (typeof allValuesId === 'string' && allValuesId.length > 0 && item.id === allValuesId) {
            // If "All" is already selected, unselect everything
            if (hasAll) {
              (onChange as (value: V[]) => void)([]);
            } else {
              // Select "All" (which means all items are selected in UI)
              (onChange as (value: V[]) => void)([allValuesId]);
            }
            if (closeOnSelect) {
              handleDropdownClose();
            }
            return;
          }

          // Handle individual item click
          if (hasAll) {
            // When "All" is selected, clicking any item means:
            // Remove "All" and the clicked item, but keep all other items selected
            // Get all available item IDs (excluding "All" and the clicked item)
            const allItemIds = (requestData?.data ?? [])
              .map((dataItem) => dataItem.id)
              .filter((id) => id !== allValuesId && id !== item.id) as V[];
            (onChange as (value: V[]) => void)(allItemIds);
          } else {
            // Normal case: "All" is not selected
            const isItemSelected = currentArray.indexOf(item.id) > -1;

            if (isItemSelected) {
              // Item is in the array, so unselect it
              const newArray = currentArray.filter((id) => id !== item.id);
              (onChange as (value: V[]) => void)(newArray);
            } else {
              // Item is not in the array, so select it
              const newArray = [...new Set([...currentArray, item.id])];
              (onChange as (value: V[]) => void)(newArray);
            }
          }
        } else {
          // Single select mode: if clicking the already selected item, select allValuesId (if provided)
          if (item.id === (value as V) && typeof allValuesId === 'string' && allValuesId.length > 0) {
            (onChange as (value: V) => void)(allValuesId as V);
          } else if (item.id !== (value as V)) {
            (onChange as (value: V) => void)(item.id);
          }
        }
        if (closeOnSelect) {
          handleDropdownClose();
        }
      }
    },
    [
      multiple,
      closeOnSelect,
      value,
      onChange,
      allValuesId,
      requestData?.data,
      handleDropdownClose,
      showCancelConfirmButtons,
      setPendingValue,
      pendingValue,
    ]
  );

  const handleSelectItem = customHandleChange ? customHandleChange : handleChange;

  const handleConfirm = useCallback(() => {
    if (showCancelConfirmButtons && pendingValue !== null) {
      if (multiple) {
        const pendingArray = pendingValue as V[];
        // If all items are unchecked and allValuesId is set, select "All" instead of empty
        if (pendingArray.length === 0 && typeof allValuesId === 'string' && allValuesId.length > 0) {
          (onChange as (value: V[]) => void)([allValuesId as V]);
        } else {
          (onChange as (value: V[]) => void)(pendingArray);
        }
      } else {
        (onChange as (value: V) => void)(pendingValue as V);
      }
    }
    // Reset search term on confirm (changes are applied, start fresh)
    setSearchTerm?.('');
    onConfirm?.();
    handleDropdownClose();
  }, [
    showCancelConfirmButtons,
    pendingValue,
    multiple,
    onChange,
    onConfirm,
    handleDropdownClose,
    allValuesId,
    setSearchTerm,
  ]);

  // Check if there are changes to enable/disable confirm button
  const hasChanges = useMemo(() => {
    if (!showCancelConfirmButtons || pendingValue === null || originalValue === null) {
      return false;
    }
    if (multiple) {
      const pendingArray = pendingValue as V[];
      const originalArray = originalValue as V[];
      if (pendingArray.length !== originalArray.length) return true;
      return !pendingArray.every((id) => originalArray.includes(id));
    } else {
      return pendingValue !== originalValue;
    }
  }, [showCancelConfirmButtons, pendingValue, originalValue, multiple]);

  // Get the value to use for rendering (pending or actual)
  const displayValue = useMemo(() => {
    if (showCancelConfirmButtons && pendingValue !== null) {
      return pendingValue;
    }
    return value;
  }, [showCancelConfirmButtons, pendingValue, value]);

  const rowRenderer = useCallback(
    (index: number, item: any) => {
      if (!item || !item.id) {
        return null;
      }

      const isItemDisabled =
        multiple && !allowNoSelection && (displayValue as V[])?.length === 1 && (displayValue as V[])?.[0] === item.id;

      // For multiple select: if "All" is selected, show all items as selected (except "All" itself)
      // For single select: normal behavior
      let isSelected: boolean;
      if (multiple) {
        const displayArray = (displayValue as V[]) ?? [];
        const hasAll = typeof allValuesId === 'string' && allValuesId.length > 0 && displayArray.includes(allValuesId);

        if (hasAll && item.id !== allValuesId) {
          // When "All" is selected, show all items as selected
          isSelected = true;
        } else {
          // Normal selection check
          isSelected = displayArray.indexOf(item.id) > -1;
        }
      } else {
        isSelected = item.id === displayValue;
      }

      return (
        <Fragment>
          <ButtonBase<'div'>
            css={css`
              height: auto;
              min-height: 36px;
              width: 100%;
              max-width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: center;
              column-gap: 12px;
              background-color: ${isSelected ? 'rgba(93, 183, 121, 0.10)' : '#FFFFFF'};
              padding: 8px 12px;
              box-shadow: none;
              outline: none;
              word-wrap: break-word;
              overflow-wrap: break-word;
              flex-wrap: wrap;

              &:hover,
              &:focus {
                box-shadow: none;
                outline: none;
                background-color: ${isSelected ? 'rgba(93, 183, 121, 0.10)' : 'rgba(0, 0, 0, 0.04)'};
              }
            `}
            tabIndex={0}
            disabled={disabled || isItemDisabled}
            onClick={() => handleSelectItem(item)}
            component="div"
          >
            {multiple && (
              <QSCheckbox
                checked={isSelected}
                onClick={
                  !isItemDisabled
                    ? (e) => {
                        e.stopPropagation();
                        handleSelectItem(item);
                      }
                    : undefined
                }
                sx={{
                  padding: 0,
                  color: '#717171',
                }}
                disabled={isItemDisabled}
              />
            )}
            {renderItemPrefix?.(item)}
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 400;
                line-height: 1.5;
                color: #2d2d2d;
                white-space: initial;
                word-wrap: break-word;
                word-break: break-all;
                overflow-wrap: break-word;
                flex: 1;
              `}
            >
              {getDisplayName(item) ?? ''}
            </QSTypography>
          </ButtonBase>
        </Fragment>
      );
    },
    [
      multiple,
      displayValue,
      handleSelectItem,
      getDisplayName,
      disabled,
      allowNoSelection,
      allValuesId,
      renderItemPrefix,
    ]
  );

  return (
    <Box
      css={css(
        `
          width: 100%;
          max-width: 250px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          row-gap: 8px;

          @media (min-width: 0px) and (max-width: 599px) {
            max-width: calc(50% - 8px);
          }
        `,
        containerStyles
      )}
    >
      {typeof popoverLabel === 'string' && popoverLabel.length > 0 && (
        <QSTypography
          css={css(
            `
              max-width: 100%;
              color: ${disabled ? 'rgba(0, 0, 0, 0.38)' : '#2D2D2D'};
              font-size: 14px;
              font-weight: 400;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
              word-wrap: break-word;
              word-break: break-all;
            `,
            popoverLabelStyles
          )}
        >
          {popoverLabel}
        </QSTypography>
      )}
      <ButtonBase<'div'>
        ref={anchorEl}
        css={css(
          `
            height: 40px;
            width: 100%;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            flex: 1 1 auto;
            padding: 8px 32px 8px 12px;
            color: inherit;
            border: 1px solid ${isDropdownOpen ? '#2d2d2d' : '#E0E0EA'};
            background-color: #FFFFFF;
            border-radius: 4px;
            position: relative;
            user-select: none;
            box-shadow: none;
            outline: none;
            cursor: pointer;

            &:hover {
              box-shadow: none;
              outline: none;
              border-color: ${hoverBorderColor};
            }

            &:focus {
              box-shadow: none;
              outline: none;
            }
          `,
          buttonStyles
        )}
        tabIndex={0}
        onClick={handleDropdownOpen}
        disabled={disabled}
        disableRipple={true}
        component="div"
      >
        <QSTypography
          css={css(
            `
              max-width: 100%;
              color: ${disabled ? 'rgba(0, 0, 0, 0.38)' : '#2D2D2D'};
              font-size: 14px;
              font-weight: 400;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
              word-wrap: break-word;
              word-break: break-all;
              width: ${showCloseIcon ? `calc(100% - 16px)` : `unset`};
              margin-right: 4px;
            `,
            placeholderStyles
          )}
        >
          {popoverValue ?? (value as string) ?? placeholder ?? 'Please select a value'}
        </QSTypography>
        {showCloseIcon && Array.isArray(value) && value.length > 0 ? (
          <CloseIcon
            css={css`
              width: 16px;
              min-width: 16px;
              height: 16px;
              min-height: 16px;
              display: inline-block;
              fill: currentColor;
              color: rgba(0, 0, 0, 0.54);
              font-size: 1.5rem;
              cursor: pointer;
              position: absolute;
              right: 27px;
              top: calc(50% - 0.35em);
              user-select: none;
              margin-right: 3px;
            `}
            onClick={(e) => {
              e.stopPropagation();
              onCloseIconClick?.();
            }}
          />
        ) : null}

        <ArrowExpandIcon
          color="#7a7a7a"
          size={28}
          css={[
            css`
              width: 28px;
              min-width: 28px;
              height: 28px;
              min-height: 28px;
              display: inline-block;
              color: rgba(0, 0, 0, 0.54);
              font-size: 1.5rem;
              pointer-events: none;
              position: absolute;
              right: 7px;
              top: calc(50% - 0.55em);
              user-select: none;
              transform: rotateZ(${isDropdownOpen ? 180 : 0}deg);
              transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
            caretIconCss,
          ]}
        />
      </ButtonBase>
      <Popover
        open={isDropdownOpen}
        anchorEl={anchorEl.current}
        disablePortal={disablePortal}
        onClose={handleDropdownClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: showCancelConfirmButtons
            ? {
                borderRadius: '8px',
                border: '1px solid #1e293b24',
                boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 4px 6px -1px rgba(0, 0, 0, 0.10)',
                width: paperWidth ? paperWidth : 'max-content',
                display: anchorEl.current?.clientWidth ? 'block' : 'none',
                minWidth: anchorEl.current?.clientWidth ?? '0px',
              }
            : {
                width: paperWidth ? paperWidth : 'max-content',
                display: anchorEl.current?.clientWidth ? 'block' : 'none',
                minWidth: anchorEl.current?.clientWidth ?? '0px',
              },
        }}
      >
        {!disableSearch && (
          <Box
            sx={{
              borderBottom: showCancelConfirmButtons ? '1px solid #1e293b24' : '1px solid #E0E0EA',
              padding: showCancelConfirmButtons ? '6px 12px' : '0px',
            }}
          >
            {showCancelConfirmButtons ? (
              <QSSearchBar
                onChange={(e) => {
                  setSearchTerm?.(e);
                }}
                value={searchInputValue ?? searchTerm ?? ''}
                showSearchIcon={true}
                closeIconSx={{
                  height: '20px',
                  width: '20px',
                  svg: {
                    height: '16px',
                    width: '16px',
                  },
                }}
                placeholder={placeholder ?? 'Search...'}
                searchIconProps={{
                  size: 16,
                  color: '#7a7a7a',
                }}
              />
            ) : (
              <Box
                sx={{
                  height: '40px',
                  lineHeight: '40px',
                  width: '100%',
                  borderBottom: '1px solid #E0E0EA',
                  overflow: 'hidden',
                  padding: '0px 12px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <QSSearchBar
                  onChange={(e) => {
                    setSearchTerm?.(e);
                  }}
                  value={searchInputValue ?? searchTerm ?? ''}
                  showSearchIcon={true}
                  placeholder={placeholder ?? 'Search...'}
                  searchIconProps={{
                    size: 16,
                    color: '#7a7a7a',
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        {customButton ? customButton : null}
        <Box
          sx={{
            overflow: 'hidden',
          }}
        >
          <Virtuoso<T>
            style={{
              height:
                !requestData?.data || requestData?.data?.length === 0
                  ? '104px'
                  : paperHeight
                  ? paperHeight
                  : showCancelConfirmButtons
                  ? '200px'
                  : '185px',
              width: '100%',
            }}
            ref={virtualListRef}
            endReached={fetchMore}
            totalCount={
              typeof refresh === 'function'
                ? moveSelectedToTop
                  ? sortDataWithSelectedFirst(requestData?.data ?? [], value).length
                  : (requestData?.data ?? []).length
                : filteredRequestData.length + 1
            }
            data={
              typeof refresh === 'function'
                ? moveSelectedToTop
                  ? sortDataWithSelectedFirst(requestData?.data ?? [], value)
                  : requestData?.data ?? []
                : filteredRequestData
            }
            itemContent={rowRenderer}
            overscan={300}
            increaseViewportBy={300}
            components={{
              EmptyPlaceholder: () => {
                if (disabled || requestData?.loading || requestData?.refreshing) {
                  return (
                    <Box
                      css={css`
                        width: 100%;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        padding: 16px;
                      `}
                    >
                      <Loader size={24} />
                    </Box>
                  );
                }
                return (
                  <EmptyDataMessage
                    message={
                      requestData?.error?.message ||
                      (typeof refresh !== 'function'
                        ? optionsEmptyMessage || 'No options to display'
                        : optionsEmptyMessage || 'Something went wrong')
                    }
                    messageStyles={emptyDataMessageStyles}
                    containerStyles={emptyDataContainerStyles}
                  />
                );
              },
              Footer: () => {
                if ((requestData as PaginatedQueryRequest<T, K>)?.isFetchingMore) {
                  return <Loader size={24} />;
                }

                if ((requestData as PaginatedQueryRequest<T, K>)?.error) {
                  return (
                    <Box
                      css={css`
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                      `}
                    >
                      <QSTypography
                        css={css`
                          font-size: 14px;
                        `}
                      >
                        {(requestData as PaginatedQueryRequest<T, K>)?.error?.message || 'Something went wrong'}
                      </QSTypography>
                      <QSButton
                        variant="text"
                        onClick={fetchMore}
                        css={css`
                          background: none;
                        `}
                      >
                        RETRY
                      </QSButton>
                    </Box>
                  );
                }

                return null;
              },
            }}
          />
        </Box>
        {customButtonBottom ? customButtonBottom : null}
        {showCancelConfirmButtons && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
              borderTop: '1px solid #E0E0E9',
            }}
          >
            <QSButton
              css={css`
                display: flex;
                height: 40px;
                min-width: 64px;
                padding: 8px 16px;
                justify-content: center;
                align-items: center;
                gap: 8px;
                border-radius: 4px;
                background: #fff;
                box-shadow: none;
                color: #2d2d2d;
                border: 1px solid #e0e0e9;
                :hover {
                  box-shadow: none;
                  background: #fcfcfc;
                }
              `}
              onClick={handleCancel}
            >
              <Box
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                {cancelButtonText}
              </Box>
            </QSButton>
            <QSButton
              css={css`
                display: flex;
                height: 40px;
                min-width: 64px;
                padding: 8px 16px;
                justify-content: center;
                align-items: center;
                gap: 8px;
                border-radius: 4px;
                background: var(--primary-main, #3f906d);
                box-shadow: none;
                :hover {
                  box-shadow: none;
                }
              `}
              onClick={handleConfirm}
              disabled={isConfirmDisabled || !hasChanges}
            >
              <Box
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                {confirmButtonText}
              </Box>
            </QSButton>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default DropdownVirtualListWithConfirm;
